import { isEmpty, isNil, isNumber } from 'lodash';
import { API } from '@shared/types/api';
import { EditableField, Field } from './Field';
import { NumberFormat } from './NumberFormat';

/*
Number formatting rules.

There are 3 formats: Basic, Currency, Percent. And 3 states: input, saved value, display.

- Basic: no difference between input, saved value, and display. All can be float. 1.23 = 1.23, 123 = 123, 123.00 = 123.00
- Currency:
  - Input can be float. If no decimal is provided, .00 is assumed.
  - Saved value is always an integer (cents). 1.23 = 123, 123 = 12300, 123.00 = 12300
  - Display is formatted currency. This looks like input, but always with a decimal and prepended with a dollar sign.
- Percent:
  - Input can be float.
  - Saved value is input / 100;
  - Display is a float converting decimal to percent, saved value * 100 followed by a %. 1.23 = 123%, 123 = 12300%, 123.00 = 12300%
*/

export class NumberField implements EditableField {
  readonly format: API.NumberFormat;
  readonly value: number;
  readonly fieldType = 'NumberField';

  constructor(value: number, format: API.NumberFormat) {
    this.value = value;
    this.format = format;
  }

  get editableValue(): string {
    return NumberField.toEditableValue(this.value, this.format);
  }

  get formattedValue(): string {
    return NumberField.toFormattedValue(this.value, this.format);
  }

  validate(): string | null {
    return null;
  }

  isEqual(field: Field): boolean {
    return (
      field instanceof NumberField && this.value === field.value && this.format === field.format
    );
  }

  toFieldJson() {
    return this.value;
  }

  toDisplayString(): string {
    return this.formattedValue;
  }

  get isEmpty() {
    return isNil(this.value);
  }

  static fromFieldJson(jsonBlob: any, format: API.NumberFormat): NumberField | null {
    if (isNumber(jsonBlob)) {
      return new NumberField(jsonBlob, format);
    } else {
      return null;
    }
  }

  static toEditableValue(value: number | null | undefined, format: API.NumberFormat): string {
    if (isNil(value) || isNaN(value)) {
      return '';
    }

    switch (format) {
      case 'CURRENCY': {
        const isWholeNumber = Math.round(value / 100) === value / 100;
        const formatter = isWholeNumber
          ? NumberFormat.editableWholeCurrency
          : NumberFormat.editableFractionalCurrency;
        return formatter(value / 100);
      }
      case 'PERCENT':
        return NumberFormat.editableFloat(value * 100);
      default:
        return NumberFormat.editableFloat(value);
    }
  }

  static toFormattedValue(value: number | null | undefined, format: API.NumberFormat): string {
    if (isNil(value) || isNaN(value)) {
      return '';
    }

    switch (format) {
      case 'CURRENCY':
        return NumberFormat.currency(value / 100);
      case 'PERCENT':
        return NumberFormat.percent(value);
      default:
        return NumberFormat.float(value);
    }
  }

  static toStoredValue(editableValue: string, format: API.NumberFormat): number | null {
    try {
      // remove all non-numeric input
      const numericString = NumberField.removeNonNumericInput(editableValue);
      if (isEmpty(numericString)) {
        return null;
      }

      let numericValue = Number(numericString);
      switch (format) {
        case 'CURRENCY':
          numericValue = Math.round(numericValue * 100);
          break;
        case 'PERCENT':
          numericValue = Number(NumberFormat.editableFloat(numericValue)) / 100;
          break;
        default: {
          numericValue = Number(NumberFormat.editableFloat(numericValue));
        }
      }

      // ensure that the above format handling still returns a number
      if (isNaN(numericValue)) {
        return null;
      }
      return numericValue;
    } catch (error) {
      console.warn('Error parsing number', error);
      return null;
    }
  }

  static removeInvalidInput(input: string): string {
    // remove all non-numeric input, allowing number-related characters
    return input.replace(/[^\d.,$% -]/g, '');
  }

  static removeNonNumericInput(input: string): string {
    // remove all non-numeric input
    return input.replace(/[^\d.-]/g, '');
  }
}
